import clsx from "clsx";
import type { ComponentProps, ReactNode } from "react";

// font-literal: what someone types into a control is data, not prose, so the
// font's contextual alternates stay out of it (see application.css).
const CONTROL =
  "w-full border-2 border-line bg-surface-plain px-3 py-2 font-body text-body text-ink shadow-control " +
  "font-literal placeholder:text-ink-soft placeholder:italic " +
  "focus:outline-none focus:ring-2 focus:ring-viking focus:ring-offset-1 focus:ring-offset-bg " +
  "disabled:cursor-not-allowed disabled:opacity-50";

export function Label({
  children,
  hint,
  htmlFor,
}: {
  children: ReactNode;
  hint?: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <label
        htmlFor={htmlFor}
        className="font-display text-subheading text-ink font-bold"
      >
        {children}
      </label>
      {hint && <span className="text-caption text-ink-soft">{hint}</span>}
    </div>
  );
}

export function FieldError({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return (
    <span className="text-caption text-sunset-orange font-bold">
      {children}
    </span>
  );
}

// A labelled control wrapper - keeps label/hint/error spacing consistent.
export function Field({
  label,
  hint,
  error,
  children,
}: {
  label?: string;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && <Label hint={hint}>{label}</Label>}
      {children}
      <FieldError>{error}</FieldError>
    </div>
  );
}

export function Input({ className, ...props }: ComponentProps<"input">) {
  return <input className={clsx(CONTROL, className)} {...props} />;
}

export function Textarea({ className, ...props }: ComponentProps<"textarea">) {
  return <textarea className={clsx(CONTROL, className)} {...props} />;
}

export function Select({ className, ...props }: ComponentProps<"select">) {
  return <select className={clsx(CONTROL, className)} {...props} />;
}

// appearance-none plus a drawn checkmark, because an explicit background paints
// over the native checked state - a ticked box was rendering as empty.
export function Checkbox({ className, ...props }: ComponentProps<"input">) {
  return (
    <input
      type="checkbox"
      className={clsx(
        "border-line bg-surface-plain relative h-5 w-5 shrink-0 cursor-pointer appearance-none border-2",
        "checked:bg-leaf",
        "after:absolute after:inset-0 after:flex after:items-center after:justify-center",
        "after:text-[13px] after:leading-none after:font-bold after:text-white after:content-['']",
        "checked:after:content-['✓']",
        "focus:ring-viking focus:ring-offset-bg focus:ring-2 focus:ring-offset-1 focus:outline-none",
        className,
      )}
      {...props}
    />
  );
}
